import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { config } from '@/config/config';
import { resolveLocale } from '@/utils/resolveLocale';

// #525 — the UI language follows the browser on every load; a manual switch
// lasts the session only. Older builds persisted the switch under this key,
// which pinned English on French phones; the key is removed, never read.
const LEGACY_LOCALE_KEY = 'locale';

function _browserLocale(): string {
  const tag = typeof navigator !== 'undefined' ? navigator.language : undefined;
  return resolveLocale(tag, config.locale.supported, config.locale.fallback);
}

// #8 contextual proximity defaults per the issue design.
const DEFAULT_LATERAL_NM = 10;
const DEFAULT_VERTICAL_FT = 5000;

function _readInt(key: string, fallback: number): number {
  const raw = localStorage.getItem(key);
  if (raw === null) return fallback;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

export type TrackingDetent = 'peek' | 'half' | 'tall';
// #537 — the sheet opens at half on every visit so the map is always there
// above the list. Older builds persisted the last detent under this key,
// which brought returning users back to a list covering the map; the key
// is removed, never read. The detent still lives in this store (not the
// view) so it survives a route change within the session.
const DEFAULT_DETENT: TrackingDetent = 'half';
const LEGACY_DETENT_KEY = 'trackingDetent';

export const useSettingsStore = defineStore('settings', () => {
  // State
  localStorage.removeItem(LEGACY_LOCALE_KEY);
  localStorage.removeItem(LEGACY_DETENT_KEY);
  const locale = ref(_browserLocale());
  const pushEnabled = ref(localStorage.getItem('pushEnabled') === 'true');
  const pushSubscription = ref<PushSubscription | null>(null);

  // #8 — proximity bubble. Pilot can dial down for tight low-altitude regimes
  // or up for cruise awareness. Persisted to localStorage so the next session
  // remembers the choice.
  const contextualLateralNm = ref(_readInt('contextualLateralNm', DEFAULT_LATERAL_NM));

  // #477: which detent the tracking sheet was left at. Held here rather
  // than in the view, because /tracking is not kept alive — the view is
  // rebuilt on every entry, so component state would silently return the
  // pilot to the default every time they checked Settings. Session only
  // since #537: a reload opens at half.
  const trackingDetent = ref<TrackingDetent>(DEFAULT_DETENT);
  const contextualVerticalFt = ref(_readInt('contextualVerticalFt', DEFAULT_VERTICAL_FT));

  // Watch push enabled changes and persist
  watch(pushEnabled, (enabled) => {
    localStorage.setItem('pushEnabled', String(enabled));
  });

  watch(contextualLateralNm, (v) => {
    localStorage.setItem('contextualLateralNm', String(v));
  });
  watch(contextualVerticalFt, (v) => {
    localStorage.setItem('contextualVerticalFt', String(v));
  });

  // Actions
  // Session-only by design (#525): no persistence, and an unsupported tag is
  // ignored rather than leaving the UI with no messages.
  const setLocale = (newLocale: string) => {
    if (config.locale.supported.includes(newLocale)) locale.value = newLocale;
  };

  const setPushEnabled = (enabled: boolean) => {
    pushEnabled.value = enabled;
  };

  const setPushSubscription = (subscription: PushSubscription | null) => {
    pushSubscription.value = subscription;
  };

  const setTrackingDetent = (detent: TrackingDetent) => {
    trackingDetent.value = detent;
  };

  const setContextualLateralNm = (nm: number) => {
    if (nm > 0 && nm <= 100) contextualLateralNm.value = nm;
  };

  const setContextualVerticalFt = (ft: number) => {
    if (ft > 0 && ft <= 30000) contextualVerticalFt.value = ft;
  };

  return {
    // State
    locale,
    pushEnabled,
    pushSubscription,
    contextualLateralNm,
    contextualVerticalFt,
    trackingDetent,

    // Actions
    setLocale,
    setPushEnabled,
    setPushSubscription,
    setContextualLateralNm,
    setContextualVerticalFt,
    setTrackingDetent
  };
});
